/**
 * Engine bootstrap: prefer WebGPU, fall back to WebGL2.
 *
 * WebGPU is our day-1 target render path (best performance, modern desktop and
 * Android browsers). But iOS Safari / WKWebView and older devices may lack it
 * OR claim support and then fail during init, so a WebGL2 fallback is mandatory,
 * not optional. Both paths drive the exact same Babylon Scene API, so nothing
 * else in the app cares which one ran.
 *
 * Critically: WebGPU `initAsync()` can THROW even when `IsSupportedAsync` was
 * true (iOS Safari quirks, blocked twgsl/glslang CDN fetch, driver issues). If
 * we let that bubble up the whole app shows a blank screen. So we catch it and
 * fall back to WebGL2 — a degraded-but-working app beats nothing.
 */
import { Engine } from "@babylonjs/core/Engines/engine.js";
import { WebGPUEngine } from "@babylonjs/core/Engines/webgpuEngine.js";
import type { AbstractEngine } from "@babylonjs/core/Engines/abstractEngine.js";

export type Backend = "webgpu" | "webgl2";

export interface CreatedEngine {
  engine: AbstractEngine;
  backend: Backend;
  /** Human-readable reason we ended up on the fallback, if we did. */
  note?: string;
}

const createWebGL2 = (canvas: HTMLCanvasElement): AbstractEngine =>
  new Engine(canvas, true, {
    preserveDrawingBuffer: false,
    stencil: true,
    // Cap DPR cost on high-density mobile screens; tune in Phase 1 perf pass.
    adaptToDeviceRatio: true,
  });

/**
 * Creates the best available engine for `canvas`.
 * Honors `?webgl=1` in the URL to force the fallback path for testing.
 */
export const createEngine = async (
  canvas: HTMLCanvasElement,
): Promise<CreatedEngine> => {
  const forceWebGL = new URLSearchParams(location.search).has("webgl");

  if (forceWebGL) {
    return { engine: createWebGL2(canvas), backend: "webgl2", note: "forced via ?webgl=1" };
  }

  let webgpuSupported = false;
  try {
    webgpuSupported = await WebGPUEngine.IsSupportedAsync;
  } catch {
    webgpuSupported = false;
  }

  if (webgpuSupported) {
    try {
      const engine = new WebGPUEngine(canvas, {
        antialias: true,
        adaptToDeviceRatio: true,
      });
      await engine.initAsync();
      return { engine, backend: "webgpu" };
    } catch (err) {
      // WebGPU said "supported" but initialization failed — common on iOS
      // Safari. Don't blank the screen; degrade to WebGL2.
      const reason = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.warn(`[AllStars] WebGPU init failed, falling back to WebGL2: ${reason}`);
      return {
        engine: createWebGL2(canvas),
        backend: "webgl2",
        note: `WebGPU init failed: ${reason}`,
      };
    }
  }

  return {
    engine: createWebGL2(canvas),
    backend: "webgl2",
    note: "WebGPU not supported by this browser",
  };
};
